/**
 * i18n layer for error messages, keyed by the STABLE error codes the backend
 * emits (see ERROR-CODES.md in order-service-core / customer-service-core and
 * the validator codes from coupon/loyalty services).
 *
 * The backend always sends messages in English; the frontend decides whether
 * to show the server message or its own translation. Codes are the contract —
 * never match on message text.
 */

export type ErrorLocale = "pt-BR" | "en-US" | "es"

/** Semantic message keys, shared by every locale dictionary. */
export type MessageKey =
  | "couponNotFound"
  | "couponInactive"
  | "couponExpired"
  | "couponMaxUses"
  | "couponMinPurchase"
  | "couponNotEligible"
  | "couponAlreadyUsed"
  | "couponOutsideAvailability"
  | "couponFirstPurchaseOnly"
  | "couponCancelled"
  | "loyaltyInsufficient"
  | "benefitUnavailable"
  | "merchantClosed"
  | "storePaused"
  | "addressNotLocated"
  | "deliveryFeeChanged"
  | "insufficientStock"
  | "itemUnavailable"
  | "itemMenuChanged"
  | "deliveryUnavailable"
  | "paymentFailed"
  | "paymentMismatch"
  | "paymentMethodNotAccepted"
  | "paymentTypeNotAllowed"
  | "discountInvalid"
  | "discountExceedsTotal"
  | "optionGroupInvalid"
  | "cartNotFound"
  | "stepCoupon"
  | "stepLoyalty"
  | "stepMerchant"
  | "stepStock"
  | "genericRejection"
  | "cancelledDuringValidation"
  | "checkoutTimeout"
  | "networkError"
  | "staleCart"
  | "sessionExpired"
  | "duplicateOrder"
  | "tooManyAttempts"
  | "serviceUnstable"
  | "genericRetry"
  | "invalidRequest"
  | "notFound"
  | "conflict"
  | "notAllowed"
  | "timeout"
  | "orderNotFound"
  | "orderStateNotAllowed"
  | "cartItemQuantity"
  | "cartEmpty"
  | "belowMinimumOrder"
  | "paymentInvalid"
  | "addressInvalid"
  | "addressNotFound"
  | "nameRequired"
  | "emailInvalid"
  | "userNotFound"
  | "emailInUse"
  | "merchantNoResponse"
  | "validationTimeout"
  | "cancellationReasonInvalid"
  | "scheduledPaymentNotAccepted"
  | "schedulingNotAllowed"
  | "instantNotAllowed"
  | "slotNotOffered"
  | "windowCapacityReached"
  | "cancelScheduledNotAllowed"
  | "cancelScheduledNotScheduled"
  | "cancelScheduledPreparationStarted"
  | "cancelScheduledTerminal"
  | "cancelScheduledTooLate"
  | "cancelScheduledFailed"

/**
 * Stable backend code → semantic key. Covers the validator-service codes
 * (COUPON_*, saga rejections), the order-service-core reason codes (ORD-*)
 * and the legacy broad codes still emitted in metadata, so the translation
 * works regardless of which layer produced the error.
 */
const CODE_TO_KEY: Record<string, MessageKey> = {
  COUPON_NOT_FOUND: "couponNotFound",
  COUPON_INACTIVE: "couponInactive",
  COUPON_EXPIRED: "couponExpired",
  COUPON_MAX_USES_REACHED: "couponMaxUses",
  COUPON_MIN_PURCHASE_NOT_MET: "couponMinPurchase",
  COUPON_NOT_ELIGIBLE: "couponNotEligible",
  COUPON_ALREADY_USED: "couponAlreadyUsed",
  COUPON_OUTSIDE_AVAILABILITY: "couponOutsideAvailability",
  COUPON_NOT_FIRST_PURCHASE: "couponFirstPurchaseOnly",
  "CPN-CPN-4011": "couponFirstPurchaseOnly",
  COUPON_CANCELLED: "couponCancelled",
  "CPN-CPN-4023": "couponCancelled",

  LOYALTY_POINTS_INSUFFICIENT: "loyaltyInsufficient",
  INSUFFICIENT_POINTS_BALANCE: "loyaltyInsufficient",

  INSUFFICIENT_POINTS: "loyaltyInsufficient",
  UNKNOWN_DISCOUNT_CODE: "benefitUnavailable",
  BENEFIT_NOT_ELIGIBLE: "benefitUnavailable",
  BENEFIT_AMOUNT_MISMATCH: "benefitUnavailable",
  BENEFIT_CONFIG_NOT_FOUND: "benefitUnavailable",
  BENEFIT_CONFIG_INACTIVE: "benefitUnavailable",
  BENEFIT_TIER_STACKING_VIOLATION: "benefitUnavailable",
  BENEFIT_STACKING_POLICY_VIOLATION: "benefitUnavailable",
  ENTITLEMENT_NOT_FOUND: "benefitUnavailable",
  ENTITLEMENT_EXPIRED: "benefitUnavailable",
  ENTITLEMENT_ALREADY_USED: "benefitUnavailable",

  MERCHANT_CLOSED: "merchantClosed",
  INSUFFICIENT_STOCK: "insufficientStock",
  ITEM_UNAVAILABLE: "itemUnavailable",
  DELIVERY_UNAVAILABLE: "deliveryUnavailable",
  PAYMENT_FAILED: "paymentFailed",
  PAYMENT_MISMATCH: "paymentMismatch",
  INVALID_COUPON: "couponNotFound",
  DISCOUNT_INVALID: "discountInvalid",
  DISCOUNT_EXCEEDS_TOTAL: "discountExceedsTotal",
  CART_NOT_FOUND: "cartNotFound",

  "ORD-CRT-2001": "cartNotFound",
  "ORD-CRT-2002": "itemUnavailable",
  "ORD-CRT-2003": "optionGroupInvalid",
  "ORD-CHK-1003": "optionGroupInvalid",
  "ORD-CHK-1008": "optionGroupInvalid",
  "ORD-CHK-1009": "optionGroupInvalid",
  "ORD-CHK-2001": "itemUnavailable",
  "ORD-CHK-2002": "optionGroupInvalid",
  "ORD-CHK-4001": "insufficientStock",
  "ORD-CHK-4002": "itemUnavailable",
  "ORD-CHK-4003": "merchantClosed",
  "ORD-CHK-4004": "deliveryUnavailable",
  "ORD-CHK-4005": "paymentFailed",
  "ORD-CHK-4006": "paymentMismatch",
  "ORD-CHK-4007": "discountInvalid",
  "ORD-CHK-4008": "discountExceedsTotal",
  "ORD-CHK-4009": "loyaltyInsufficient",
  "ORD-CHK-4010": "couponNotFound",
  "ORD-CHK-4011": "couponExpired",
  "ORD-CHK-4012": "couponInactive",
  "ORD-CHK-4013": "couponMaxUses",
  "ORD-CHK-4014": "couponAlreadyUsed",
  "ORD-CHK-4015": "couponNotEligible",
  "ORD-CHK-4016": "couponNotEligible",
  "ORD-CHK-4017": "paymentMethodNotAccepted",
  "ORD-CHK-4018": "paymentTypeNotAllowed",
  "ORD-CHK-4019": "paymentTypeNotAllowed",
  "ORD-CHK-4020": "itemMenuChanged",
  "ORD-CHK-4021": "belowMinimumOrder",
  "ORD-CHK-4022": "deliveryFeeChanged",
  "ORD-CHK-4023": "merchantNoResponse",
  "ORD-CHK-4024": "merchantNoResponse",

  "ORD-CHK-4030": "scheduledPaymentNotAccepted",
  "ORD-CHK-4031": "schedulingNotAllowed",
  "ORD-CHK-4032": "instantNotAllowed",
  "ORD-CHK-4033": "slotNotOffered",
  "ORD-CHK-4034": "windowCapacityReached",
  "ORD-CHK-4036": "storePaused",
  "ORD-CHK-4037": "addressNotLocated",

  INVALID_ORDER_TIMING: "slotNotOffered",
  INVALID_SCHEDULE: "slotNotOffered",

  "ORD-CHK-1001": "paymentInvalid",
  "ORD-CHK-1002": "addressInvalid",
  "ORD-CHK-1004": "invalidRequest",
  "ORD-CHK-1005": "invalidRequest",
  "ORD-CHK-1006": "paymentInvalid",
  "ORD-CHK-1007": "paymentInvalid",
  "ORD-CHK-3001": "discountInvalid",
  "ORD-CHK-6001": "serviceUnstable",
  "ORD-CHK-6002": "validationTimeout",
  "ORD-CHK-6003": "validationTimeout",

  "ORD-CRT-1001": "cartItemQuantity",
  "ORD-CRT-1002": "staleCart",
  "ORD-CRT-1003": "staleCart",
  "ORD-CRT-1004": "invalidRequest",
  "ORD-CRT-1005": "invalidRequest",
  "ORD-CRT-1006": "invalidRequest",
  "ORD-CRT-1007": "cartEmpty",
  "ORD-CRT-3001": "conflict",
  "ORD-CRT-3002": "conflict",

  "ORD-ORD-1001": "orderStateNotAllowed",
  "ORD-ORD-1002": "invalidRequest",
  "ORD-ORD-1003": "invalidRequest",
  "ORD-ORD-1004": "invalidRequest",
  "ORD-ORD-1005": "cartEmpty",
  "ORD-ORD-1006": "cartItemQuantity",
  "ORD-ORD-1007": "invalidRequest",
  "ORD-ORD-1008": "invalidRequest",
  "ORD-ORD-1009": "invalidRequest",
  "ORD-ORD-1010": "invalidRequest",
  "ORD-ORD-1011": "cancellationReasonInvalid",
  "ORD-ORD-2001": "orderNotFound",
  "ORD-ORD-2002": "notFound",
  "ORD-ORD-2003": "userNotFound",
  "ORD-ORD-3001": "duplicateOrder",
  "ORD-ORD-4001": "orderStateNotAllowed",

  "ORD-GEN-1000": "invalidRequest",
  "ORD-GEN-2000": "notFound",
  "ORD-GEN-3000": "conflict",
  "ORD-GEN-4000": "genericRejection",
  "ORD-GEN-5000": "notAllowed",
  "ORD-GEN-5001": "sessionExpired",
  "ORD-GEN-5002": "notAllowed",
  "ORD-GEN-6000": "serviceUnstable",
  "ORD-GEN-6001": "serviceUnstable",
  "ORD-GEN-6002": "timeout",
  "ORD-GEN-7000": "serviceUnstable",
  "ORD-GEN-8000": "tooManyAttempts",
  "ORD-GEN-8001": "tooManyAttempts",
  "ORD-GEN-8002": "tooManyAttempts",

  "CUS-GEN-1000": "invalidRequest",
  "CUS-GEN-1001": "invalidRequest",
  "CUS-GEN-2000": "notFound",
  "CUS-GEN-3000": "conflict",
  "CUS-GEN-4000": "genericRejection",
  "CUS-GEN-5000": "sessionExpired",
  "CUS-GEN-5001": "notAllowed",
  "CUS-GEN-6000": "serviceUnstable",
  "CUS-GEN-6001": "serviceUnstable",
  "CUS-GEN-7000": "serviceUnstable",
  "CUS-USR-1001": "nameRequired",
  "CUS-USR-1002": "emailInvalid",
  "CUS-USR-1003": "invalidRequest",
  "CUS-USR-1004": "invalidRequest",
  "CUS-USR-1005": "invalidRequest",
  "CUS-USR-1006": "invalidRequest",
  "CUS-USR-1007": "emailInvalid",
  "CUS-USR-2001": "userNotFound",
  "CUS-USR-3001": "emailInUse",
  "CUS-ADR-1001": "addressInvalid",
  "CUS-ADR-1002": "addressInvalid",
  "CUS-ADR-1003": "invalidRequest",
  "CUS-ADR-1004": "invalidRequest",
  "CUS-ADR-2001": "addressNotFound",

  "ORD-ORD-1014": "cancelScheduledNotAllowed",
}

const MESSAGES: Record<ErrorLocale, Record<MessageKey, string>> = {
  "pt-BR": {
    couponNotFound: "Cupom inválido ou não encontrado.",
    couponInactive: "Esse cupom não está mais ativo.",
    couponExpired: "Esse cupom já expirou.",
    couponMaxUses: "Esse cupom atingiu o limite de usos.",
    couponMinPurchase: "O pedido ainda não atinge o valor mínimo desse cupom.",
    couponNotEligible: "Esse cupom não está disponível pra você.",
    couponAlreadyUsed: "Você já usou esse cupom.",
    couponOutsideAvailability: "Esse cupom só vale em determinados dias e horários.",
    couponFirstPurchaseOnly: "Esse cupom vale só na primeira compra na loja.",
    couponCancelled: "Esse cupom foi cancelado pela loja.",
    loyaltyInsufficient: "Seus pontos não são suficientes pra esse resgate.",
    benefitUnavailable: "Esse benefício de fidelidade não está mais disponível para este pedido.",
    merchantClosed: "A loja está fechada agora. Volte quando ela abrir!",
    storePaused: "A loja pausou os pedidos por alguns instantes. Tente de novo daqui a pouco.",
    addressNotLocated: "Não localizamos esse endereço no mapa. Edite o endereço, confirme o ponto e tente de novo.",
    deliveryFeeChanged: "A taxa de entrega mudou. Confira o novo total e confirme de novo.",
    insufficientStock: "Algum item da sacola ficou indisponível agora.",
    itemUnavailable: "Algum item da sacola ficou indisponível agora.",
    itemMenuChanged: "Este item foi atualizado no cardápio. Atualize a página e refaça o pedido.",
    deliveryUnavailable: "A entrega não está disponível pra esse endereço agora.",
    paymentFailed: "Não conseguimos processar o pagamento. Tente outra forma.",
    paymentMismatch: "Os valores da sacola mudaram. Revise o pedido e tente de novo.",
    paymentMethodNotAccepted: "A loja não aceita essa forma de pagamento.",
    paymentTypeNotAllowed: "Essa forma de pagamento não está disponível por aqui.",
    discountInvalid: "Esse desconto não pôde ser aplicado.",
    discountExceedsTotal: "O desconto é maior que o total do pedido.",
    optionGroupInvalid: "Algum item está com opções incompletas. Revise a sacola.",
    cartNotFound: "Sua sacola expirou. Monte o pedido de novo, rapidinho.",
    stepCoupon: "Não conseguimos aplicar seu cupom. Revise e tente de novo.",
    stepLoyalty: "Não conseguimos validar seus pontos de fidelidade.",
    stepMerchant: "A loja não pôde aceitar seu pedido agora.",
    stepStock: "Algum item da sacola ficou indisponível agora.",
    genericRejection: "Seu pedido não pôde ser confirmado. Revise a sacola e tente de novo.",
    cancelledDuringValidation: "Seu pedido foi cancelado durante a validação.",
    checkoutTimeout: "Seu pedido está demorando mais que o esperado. Acompanhe em Meus pedidos.",
    networkError: "Sem conexão com a loja agora. Verifique sua internet e tente de novo.",
    staleCart: "Alguns itens mudaram desde que você montou a sacola. Refaça o pedido, rapidinho.",
    sessionExpired: "Sua sessão expirou. Entre de novo pra finalizar o pedido.",
    duplicateOrder: "Parece que esse pedido já foi enviado. Confira em Meus pedidos.",
    tooManyAttempts: "Muitas tentativas seguidas. Espere um instante e tente de novo.",
    serviceUnstable: "A loja está com instabilidade no momento. Tente de novo em instantes.",
    genericRetry: "Não deu pra concluir o pedido. Tente de novo.",
    invalidRequest: "Alguns dados não estão certos. Confira e tente de novo.",
    notFound: "Não encontramos o que você procura.",
    conflict: "Isso já existe por aqui. Atualize a página para ver como está.",
    notAllowed: "Você não tem acesso a isso.",
    timeout: "A loja demorou pra responder. Tente de novo em instantes.",
    orderNotFound: "Não encontramos esse pedido.",
    orderStateNotAllowed: "Esse pedido já passou dessa etapa.",
    cartItemQuantity: "A quantidade escolhida não é permitida para esse item.",
    cartEmpty: "Sua sacola está vazia. Adicione um item para continuar.",
    belowMinimumOrder: "O valor da sacola ainda não atinge o pedido mínimo da loja.",
    paymentInvalid: "Confira os dados de pagamento e tente de novo.",
    addressInvalid: "Esse endereço está incompleto. Revise os dados.",
    addressNotFound: "Não encontramos esse endereço.",
    nameRequired: "Informe seu nome para continuar.",
    emailInvalid: "Esse e-mail não parece válido.",
    userNotFound: "Não encontramos essa conta.",
    emailInUse: "Esse e-mail já está em uso.",
    merchantNoResponse: "A loja não confirmou seu pedido a tempo. Tente de novo em instantes.",
    validationTimeout: "Não conseguimos confirmar seu pedido a tempo. Tente de novo, por favor.",
    cancellationReasonInvalid: "Não deu pra registrar esse motivo de cancelamento. Escolha outro.",
    scheduledPaymentNotAccepted:
      "A loja não aceita essa forma de pagamento em pedido agendado. Escolha outra.",
    schedulingNotAllowed: "A loja não aceita agendamento no momento.",
    instantNotAllowed: "Esta loja só aceita pedidos agendados.",
    slotNotOffered: "Esse horário não está mais disponível na loja. Escolha outro.",
    windowCapacityReached: "Não há mais horários nessa janela.",
    cancelScheduledNotAllowed: "Não dá mais para cancelar esse agendamento por aqui. Fale com a loja.",
    cancelScheduledNotScheduled: "Esse pedido não é agendado, então o cancelamento é outro caminho.",
    cancelScheduledPreparationStarted: "A loja já começou a preparar seu pedido. Fale com ela para cancelar.",
    cancelScheduledTerminal: "Esse pedido já foi encerrado.",
    cancelScheduledTooLate: "Passou do horário de cancelar esse agendamento. Fale com a loja.",
    cancelScheduledFailed: "Não conseguimos cancelar seu agendamento agora. Tente de novo em instantes.",
  },
  "en-US": {
    couponNotFound: "Invalid or unknown coupon.",
    couponInactive: "This coupon is no longer active.",
    couponExpired: "This coupon has expired.",
    couponMaxUses: "This coupon has reached its usage limit.",
    couponMinPurchase: "Your order doesn't meet this coupon's minimum amount yet.",
    couponNotEligible: "This coupon isn't available for you.",
    couponAlreadyUsed: "You've already used this coupon.",
    couponOutsideAvailability: "This coupon is only valid on certain days and times.",
    couponFirstPurchaseOnly: "This coupon is only valid on your first order at this store.",
    couponCancelled: "This coupon was cancelled by the store.",
    loyaltyInsufficient: "You don't have enough points for this redemption.",
    benefitUnavailable: "This loyalty benefit is no longer available for this order.",
    merchantClosed: "The store is closed right now. Come back when it opens!",
    storePaused: "The store paused orders for a moment. Try again shortly.",
    addressNotLocated: "We couldn't find this address on the map. Edit it, confirm the pin and try again.",
    deliveryFeeChanged: "The delivery fee changed. Check the new total and confirm again.",
    insufficientStock: "An item in your bag just became unavailable.",
    itemUnavailable: "An item in your bag just became unavailable.",
    itemMenuChanged: "This item was updated in the menu. Refresh the page and place your order again.",
    deliveryUnavailable: "Delivery isn't available for this address right now.",
    paymentFailed: "We couldn't process the payment. Try another method.",
    paymentMismatch: "The bag totals changed. Review your order and try again.",
    paymentMethodNotAccepted: "The store doesn't accept this payment method.",
    paymentTypeNotAllowed: "This payment method isn't available here.",
    discountInvalid: "This discount couldn't be applied.",
    discountExceedsTotal: "The discount is larger than the order total.",
    optionGroupInvalid: "An item has incomplete options. Review your bag.",
    cartNotFound: "Your bag expired. Build your order again real quick.",
    stepCoupon: "We couldn't apply your coupon. Review it and try again.",
    stepLoyalty: "We couldn't validate your loyalty points.",
    stepMerchant: "The store couldn't accept your order right now.",
    stepStock: "An item in your bag just became unavailable.",
    genericRejection: "Your order couldn't be confirmed. Review your bag and try again.",
    cancelledDuringValidation: "Your order was cancelled during validation.",
    checkoutTimeout: "Your order is taking longer than expected. Track it in My orders.",
    networkError: "No connection to the store right now. Check your internet and try again.",
    staleCart: "Some items changed since you built your bag. Redo the order real quick.",
    sessionExpired: "Your session expired. Sign in again to finish the order.",
    duplicateOrder: "Looks like this order was already placed. Check My orders.",
    tooManyAttempts: "Too many attempts in a row. Wait a moment and try again.",
    serviceUnstable: "The store is having issues right now. Try again in a moment.",
    genericRetry: "We couldn't complete your order. Try again.",
    invalidRequest: "Some details aren't right. Check them and try again.",
    notFound: "We couldn't find what you're looking for.",
    conflict: "This already exists. Refresh the page to see the current state.",
    notAllowed: "You don't have access to that.",
    timeout: "The store took too long to respond. Try again in a moment.",
    orderNotFound: "We couldn't find that order.",
    orderStateNotAllowed: "This order is already past that step.",
    cartItemQuantity: "That quantity isn't allowed for this item.",
    cartEmpty: "Your bag is empty. Add an item to continue.",
    belowMinimumOrder: "Your bag hasn't reached the store's minimum order yet.",
    paymentInvalid: "Check the payment details and try again.",
    addressInvalid: "This address is incomplete. Review the details.",
    addressNotFound: "We couldn't find that address.",
    nameRequired: "Enter your name to continue.",
    emailInvalid: "That email doesn't look valid.",
    userNotFound: "We couldn't find that account.",
    emailInUse: "That email is already in use.",
    merchantNoResponse: "The store didn't confirm your order in time. Try again in a moment.",
    validationTimeout: "We couldn't confirm your order in time. Please try again.",
    cancellationReasonInvalid: "We couldn't record that cancellation reason. Pick another one.",
    scheduledPaymentNotAccepted:
      "The store doesn't accept this payment method on scheduled orders. Pick another one.",
    schedulingNotAllowed: "The store isn't taking scheduled orders right now.",
    instantNotAllowed: "This store only takes scheduled orders.",
    slotNotOffered: "That time is no longer available at the store. Pick another one.",
    windowCapacityReached: "There are no times left in this window.",
    cancelScheduledNotAllowed: "This scheduled order can no longer be cancelled here. Contact the store.",
    cancelScheduledNotScheduled: "This isn't a scheduled order, so cancelling it takes another route.",
    cancelScheduledPreparationStarted: "The store already started preparing your order. Contact them to cancel.",
    cancelScheduledTerminal: "This order is already closed.",
    cancelScheduledTooLate: "It's past the cut-off to cancel this scheduled order. Contact the store.",
    cancelScheduledFailed: "We couldn't cancel your scheduled order right now. Try again in a moment.",
  },
  es: {
    couponNotFound: "Cupón inválido o no encontrado.",
    couponInactive: "Este cupón ya no está activo.",
    couponExpired: "Este cupón ya expiró.",
    couponMaxUses: "Este cupón alcanzó su límite de usos.",
    couponMinPurchase: "El pedido aún no alcanza el monto mínimo de este cupón.",
    couponNotEligible: "Este cupón no está disponible para ti.",
    couponAlreadyUsed: "Ya usaste este cupón.",
    couponOutsideAvailability: "Este cupón solo es válido en ciertos días y horarios.",
    couponFirstPurchaseOnly: "Este cupón solo es válido en tu primera compra en esta tienda.",
    couponCancelled: "La tienda canceló este cupón.",
    loyaltyInsufficient: "No tienes puntos suficientes para este canje.",
    benefitUnavailable: "Este beneficio de fidelidad ya no está disponible para este pedido.",
    merchantClosed: "La tienda está cerrada ahora. ¡Vuelve cuando abra!",
    storePaused: "La tienda pausó los pedidos por un momento. Intenta de nuevo en breve.",
    addressNotLocated: "No encontramos esta dirección en el mapa. Edítala, confirma el punto e intenta de nuevo.",
    deliveryFeeChanged: "La tarifa de envío cambió. Revisa el nuevo total y confirma de nuevo.",
    insufficientStock: "Un artículo de tu bolsa acaba de quedar sin disponibilidad.",
    itemUnavailable: "Un artículo de tu bolsa acaba de quedar sin disponibilidad.",
    itemMenuChanged: "Este artículo se actualizó en el menú. Actualiza la página y vuelve a hacer el pedido.",
    deliveryUnavailable: "El delivery no está disponible para esta dirección ahora.",
    paymentFailed: "No pudimos procesar el pago. Prueba otro método.",
    paymentMismatch: "Los totales de la bolsa cambiaron. Revisa el pedido e inténtalo de nuevo.",
    paymentMethodNotAccepted: "La tienda no acepta este método de pago.",
    paymentTypeNotAllowed: "Este método de pago no está disponible aquí.",
    discountInvalid: "Este descuento no se pudo aplicar.",
    discountExceedsTotal: "El descuento es mayor que el total del pedido.",
    optionGroupInvalid: "Un artículo tiene opciones incompletas. Revisa tu bolsa.",
    cartNotFound: "Tu bolsa expiró. Arma el pedido de nuevo, rapidito.",
    stepCoupon: "No pudimos aplicar tu cupón. Revísalo e inténtalo de nuevo.",
    stepLoyalty: "No pudimos validar tus puntos de fidelidad.",
    stepMerchant: "La tienda no pudo aceptar tu pedido ahora.",
    stepStock: "Un artículo de tu bolsa acaba de quedar sin disponibilidad.",
    genericRejection: "Tu pedido no pudo ser confirmado. Revisa la bolsa e inténtalo de nuevo.",
    cancelledDuringValidation: "Tu pedido fue cancelado durante la validación.",
    checkoutTimeout: "Tu pedido está tardando más de lo esperado. Síguelo en Mis pedidos.",
    networkError: "Sin conexión con la tienda ahora. Revisa tu internet e inténtalo de nuevo.",
    staleCart: "Algunos artículos cambiaron desde que armaste la bolsa. Rehaz el pedido, rapidito.",
    sessionExpired: "Tu sesión expiró. Inicia sesión de nuevo para finalizar el pedido.",
    duplicateOrder: "Parece que este pedido ya fue enviado. Revisa Mis pedidos.",
    tooManyAttempts: "Demasiados intentos seguidos. Espera un momento e inténtalo de nuevo.",
    serviceUnstable: "La tienda está con problemas en este momento. Inténtalo de nuevo en un instante.",
    genericRetry: "No pudimos completar tu pedido. Inténtalo de nuevo.",
    invalidRequest: "Algunos datos no están correctos. Revísalos e inténtalo de nuevo.",
    notFound: "No encontramos lo que buscas.",
    conflict: "Esto ya existe. Actualiza la página para ver cómo está.",
    notAllowed: "No tienes acceso a eso.",
    timeout: "La tienda tardó en responder. Inténtalo de nuevo en un instante.",
    orderNotFound: "No encontramos ese pedido.",
    orderStateNotAllowed: "Este pedido ya pasó esa etapa.",
    cartItemQuantity: "Esa cantidad no está permitida para este artículo.",
    cartEmpty: "Tu bolsa está vacía. Agrega un artículo para continuar.",
    belowMinimumOrder: "Tu bolsa aún no alcanza el pedido mínimo de la tienda.",
    paymentInvalid: "Revisa los datos de pago e inténtalo de nuevo.",
    addressInvalid: "Esta dirección está incompleta. Revisa los datos.",
    addressNotFound: "No encontramos esa dirección.",
    nameRequired: "Ingresa tu nombre para continuar.",
    emailInvalid: "Ese correo no parece válido.",
    userNotFound: "No encontramos esa cuenta.",
    emailInUse: "Ese correo ya está en uso.",
    merchantNoResponse: "La tienda no confirmó tu pedido a tiempo. Inténtalo de nuevo en un instante.",
    validationTimeout: "No pudimos confirmar tu pedido a tiempo. Inténtalo de nuevo, por favor.",
    cancellationReasonInvalid: "No pudimos registrar ese motivo de cancelación. Elige otro.",
    scheduledPaymentNotAccepted:
      "La tienda no acepta esa forma de pago en pedidos programados. Elige otra.",
    schedulingNotAllowed: "La tienda no acepta pedidos programados en este momento.",
    instantNotAllowed: "Esta tienda solo acepta pedidos programados.",
    slotNotOffered: "Ese horario ya no está disponible en la tienda. Elige otro.",
    windowCapacityReached: "No quedan horarios en esa franja.",
    cancelScheduledNotAllowed: "Ya no se puede cancelar este pedido programado por aquí. Habla con la tienda.",
    cancelScheduledNotScheduled: "Este pedido no es programado, así que se cancela por otra vía.",
    cancelScheduledPreparationStarted: "La tienda ya empezó a preparar tu pedido. Habla con ella para cancelar.",
    cancelScheduledTerminal: "Este pedido ya está cerrado.",
    cancelScheduledTooLate: "Pasó el horario para cancelar este pedido programado. Habla con la tienda.",
    cancelScheduledFailed: "No pudimos cancelar tu pedido programado ahora. Inténtalo de nuevo en un instante.",
  },
}

/**
 * Resolves the locale for error messages from a BCP-47 language tag.
 * Portuguese is the product default; English and Spanish are supported.
 *
 * NOT wired to `navigator.language` — see `activeLocale` below. This is the
 * mapping a real language switcher will need; it must be fed a tag the APP
 * chose, never one read straight from the browser.
 */
export function resolveErrorLocale(language?: string): ErrorLocale {
  const tag = (language ?? (typeof navigator !== "undefined" ? navigator.language : "")).toLowerCase()
  if (tag.startsWith("en")) return "en-US"
  if (tag.startsWith("es")) return "es"
  return "pt-BR"
}

/**
 * Error copy follows the APP's language, not the browser's.
 *
 * This used to default to `resolveErrorLocale()`, i.e. `navigator.language`.
 * Every other string in the storefront is hardcoded pt-BR, so a customer
 * browsing in English got a half-translated screen: "Opa, algo deu errado" and
 * "Tentar de novo" wrapped around "You've already used this coupon.". Until the
 * whole UI is localizable, one language for the whole screen beats a correct
 * translation of one line inside it.
 *
 * When a language switcher lands, it should call `setErrorLocale` with the same
 * tag it applies to the rest of the copy.
 */
let activeLocale: ErrorLocale = "pt-BR"

/** Overrides the active locale (language switcher, tests). */
export function setErrorLocale(locale: ErrorLocale): void {
  activeLocale = locale
}

export function getErrorLocale(): ErrorLocale {
  return activeLocale
}

/** Returns the localized text for a semantic message key. */
export function errorMessage(key: MessageKey): string {
  return MESSAGES[activeLocale][key]
}

/**
 * Translates a stable backend error code into the active locale.
 * Returns null for unknown codes so callers can apply their own fallback
 * chain (step message, server message, generic).
 */
/** Message key a reason code maps to, or null for an unknown code. */
export function errorKeyForCode(code: string | undefined | null): MessageKey | null {
  if (!code) return null
  return CODE_TO_KEY[code.trim().toUpperCase()] ?? null
}

export function translateErrorCode(code: string | undefined | null): string | null {
  const key = errorKeyForCode(code)
  return key ? MESSAGES[activeLocale][key] : null
}

/**
 * Shape of every reason code in the ecosystem: `<PREFIX>-<AREA>-<NNNN>`
 * (ORD-CHK-4003, CUS-ADR-2001, MER-CAT-1002, POS-PXR-1007…).
 */
const CODE_SHAPE = /^[A-Z]{2,4}-[A-Z]{3,4}-(\d)\d{3}$/

/**
 * Category digit -> semantic key.
 *
 * The thousands digit encodes the error category; every backend catalog shares
 * the same legend. 7xxx (EXTERNAL_SERVICE) collapses into the same copy as
 * 6xxx (INTERNAL) — dependency down and we are down are the same event for
 * someone trying to order dinner.
 */
const CATEGORY_TO_KEY: Record<string, MessageKey> = {
  "1": "invalidRequest",
  "2": "notFound",
  "3": "conflict",
  "4": "genericRejection",
  "5": "notAllowed",
  "6": "serviceUnstable",
  "7": "serviceUnstable",
  "8": "tooManyAttempts",
}

/**
 * Last-resort translation for a well-formed code with no specific mapping —
 * a code from a service this app rarely touches, or one added to a catalog
 * after this file was last updated. Returns null only when the string is not
 * shaped like a reason code at all.
 */
export function translateErrorCodeByCategory(code: string | undefined | null): string | null {
  if (!code) return null
  const digit = CODE_SHAPE.exec(code.trim().toUpperCase())?.[1]
  const key = digit ? CATEGORY_TO_KEY[digit] : undefined
  return key ? MESSAGES[activeLocale][key] : null
}

/**
 * Prefer the specific message when we have one, otherwise the category message.
 * Returns null only when input is not a reason code. Kept separate from
 * `translateErrorCode` because some callers have a better local fallback.
 */
export function errorTextForCode(code: string | undefined | null): string | null {
  return translateErrorCode(code) ?? translateErrorCodeByCategory(code)
}

/**
 * The refusal reasons `ORD-ORD-1014` carries in `ErrorInfo.metadata.refusal`
 * when order-service-core rejects a customer-initiated cancellation of a
 * scheduled order (§10.1 do plano).
 */
export type ScheduledCancellationRefusal =
  | "NOT_SCHEDULED"
  | "PREPARATION_STARTED"
  | "TERMINAL"
  | "TOO_LATE"

const REFUSAL_TO_KEY: Record<ScheduledCancellationRefusal, MessageKey> = {
  NOT_SCHEDULED: "cancelScheduledNotScheduled",
  PREPARATION_STARTED: "cancelScheduledPreparationStarted",
  TERMINAL: "cancelScheduledTerminal",
  TOO_LATE: "cancelScheduledTooLate",
}

/** True for the four refusal values the contract defines; narrows unknown input. */
export function isScheduledCancellationRefusal(
  value: unknown,
): value is ScheduledCancellationRefusal {
  return typeof value === "string" && value in REFUSAL_TO_KEY
}

/**
 * Localized copy for a refused scheduled-order cancellation. An unknown or
 * absent refusal falls back to the generic `ORD-ORD-1014` sentence, so the
 * customer is never shown the backend's English.
 */
export function translateScheduledCancellationRefusal(refusal: unknown): string {
  const key = isScheduledCancellationRefusal(refusal)
    ? REFUSAL_TO_KEY[refusal]
    : "cancelScheduledNotAllowed"
  return MESSAGES[activeLocale][key]
}

/** Localized copy for a cancellation attempt that failed for a non-contract reason. */
export function scheduledCancellationFailureMessage(): string {
  return MESSAGES[activeLocale].cancelScheduledFailed
}
